import type { OutreachTemplateKey } from '@/types/lead-outreach';
import type { ProspectNurtureSendEvent } from '@/types/prospect-nurture';

/** ~3 weeks between nurture pulse issues. */
export const NURTURE_PULSE_CADENCE_DAYS = 21;

export type NurturePulseTemplateKey = Extract<
  OutreachTemplateKey,
  | 'B_pulse_01'
  | 'B_pulse_02'
  | 'B_pulse_03'
  | 'B_pulse_04'
  | 'B_pulse_05'
  | 'B_pulse_06'
>;

export interface NurturePulseIssue {
  issue: number;
  templateKey: NurturePulseTemplateKey;
  /**
   * Suggested default proof binding ONLY. Carries no send authority: the
   * server revalidates any proof against live `warm_nurture` clearance before
   * it can enter a campaign, and blocked proofs produce a blocked content
   * state — never substitute copy. (Retired defect: this map previously acted
   * as a second content authority with fallback marketing statements.)
   */
  proofMetricId: string;
  theme: string;
}

/**
 * Six-issue network pulse rotation for Track B nurture (~3 week cadence).
 * Update proof_metric_id bindings in Customer Ops as metrics are warm-cleared.
 */
export const NURTURE_PULSE_ISSUES: NurturePulseIssue[] = [
  {
    issue: 1,
    templateKey: 'B_pulse_01',
    proofMetricId: 'proof_lrp_basket_lift',
    theme: 'Basket lift on the floor',
  },
  {
    issue: 2,
    templateKey: 'B_pulse_02',
    proofMetricId: 'proof_lrp_dracut_sessions_lift',
    theme: 'Floor traffic & sessions',
  },
  {
    issue: 3,
    templateKey: 'B_pulse_03',
    proofMetricId: 'proof_lrp_dracut_spotlight_revenue',
    theme: 'Brand spotlight revenue',
  },
  {
    issue: 4,
    templateKey: 'B_pulse_04',
    proofMetricId: 'proof_lrp_tewksbury_rfid_engagement',
    theme: 'Interactive engagement',
  },
  {
    issue: 5,
    templateKey: 'B_pulse_05',
    proofMetricId: 'proof_network_checkout_lift',
    theme: 'Checkout & throughput',
  },
  {
    issue: 6,
    templateKey: 'B_pulse_06',
    proofMetricId: 'proof_network_weekly_pulse',
    theme: 'Network pulse recap',
  },
];

export const NURTURE_PULSE_TEMPLATE_KEYS = NURTURE_PULSE_ISSUES.map((row) => row.templateKey);

const PULSE_ISSUE_RE = /(?:^|\s·\s)pulse:(\d+)/i;
const PULSE_NEXT_RE = /next:(\d{4}-\d{2}-\d{2})/i;

export function parsePulseIssueFromNotes(notes: string | null | undefined): number | null {
  const match = notes?.match(PULSE_ISSUE_RE);
  if (!match?.[1]) return null;
  const issue = Number.parseInt(match[1], 10);
  return issue >= 1 && issue <= NURTURE_PULSE_ISSUES.length ? issue : null;
}

export function parsePulseNextSendDate(notes: string | null | undefined): string | null {
  return notes?.match(PULSE_NEXT_RE)?.[1] ?? null;
}

export function getNurturePulseIssue(issue: number): NurturePulseIssue | null {
  return NURTURE_PULSE_ISSUES.find((row) => row.issue === issue) ?? null;
}

export function getNurturePulseIssueByTemplate(
  templateKey: OutreachTemplateKey
): NurturePulseIssue | null {
  return NURTURE_PULSE_ISSUES.find((row) => row.templateKey === templateKey) ?? null;
}

export function getNurturePulseIssueByProofMetricId(proofMetricId: string): NurturePulseIssue | null {
  return NURTURE_PULSE_ISSUES.find((row) => row.proofMetricId === proofMetricId) ?? null;
}

function lastPulseSendIssue(events: ProspectNurtureSendEvent[]): number | null {
  for (const event of events) {
    if (event.event_type !== 'sent' && event.event_type !== 'manual_log') continue;
    const fromTemplate = event.template_key
      ? getNurturePulseIssueByTemplate(event.template_key as OutreachTemplateKey)
      : null;
    if (fromTemplate) return fromTemplate.issue;
    const fromNotes = parsePulseIssueFromNotes(event.notes);
    if (fromNotes) return fromNotes;
  }
  return null;
}

export function suggestNurturePulseIssue(input: {
  nurtureNotes?: string | null;
  sendEvents?: ProspectNurtureSendEvent[];
}): NurturePulseIssue {
  const fromNotes = parsePulseIssueFromNotes(input.nurtureNotes);
  if (fromNotes) {
    const current = getNurturePulseIssue(fromNotes);
    if (current) return current;
  }

  const lastIssue = input.sendEvents ? lastPulseSendIssue(input.sendEvents) : null;
  if (lastIssue) {
    const nextIssue = lastIssue >= NURTURE_PULSE_ISSUES.length ? 1 : lastIssue + 1;
    return getNurturePulseIssue(nextIssue) ?? NURTURE_PULSE_ISSUES[0]!;
  }

  return NURTURE_PULSE_ISSUES[0]!;
}

export function formatPulseNurtureNotes(input: {
  issue: number;
  nextSendDate?: string | null;
  existingNotes?: string | null;
}): string {
  const withoutPulse = (input.existingNotes ?? '')
    .replace(PULSE_ISSUE_RE, '')
    .replace(PULSE_NEXT_RE, '')
    .replace(/\s*·\s*$/g, '')
    .trim();

  const parts = [`pulse:${input.issue}`];
  if (input.nextSendDate) parts.push(`next:${input.nextSendDate}`);

  return withoutPulse ? `${withoutPulse} · ${parts.join(' · ')}` : parts.join(' · ');
}

export function addDaysIso(date: Date, days: number): string {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next.toISOString().slice(0, 10);
}

export function isPulseSendDue(notes: string | null | undefined, referenceDate = new Date()): boolean {
  const next = parsePulseNextSendDate(notes);
  if (!next) return true;
  return next <= referenceDate.toISOString().slice(0, 10);
}
